import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { ChatMessage } from "@/common/utils/types";
import { apiFetch } from "@/common/lib/api";

export interface ScoutChatHistory {
    id: string;
    messages: ChatMessage[];
}

// Fetch chat histories
const fetchScoutChatHistories = async (params: UseScoutChatHistoriesProps): Promise<ScoutChatHistory[]> => {
    const data = await apiFetch<{ chatHistories: ScoutChatHistory[] }>("chat/history", {
        method: "GET",
        searchParams: {
            includeMessages: true,
            ...(params.patientId && { patientId: params.patientId }),
        },
        retry: 0,
    });
    return data?.chatHistories ?? [];
};

// Create chat history mutation
interface CreateChatHistoryParams {
    patientId?: string;
}
const createChatHistory = async ({ patientId }: CreateChatHistoryParams): Promise<ScoutChatHistory> => {
    const data = await apiFetch<{ chatHistory: ScoutChatHistory }>("chat/history", {
        method: "POST",
        json: { patientId },
        retry: 0,
    });

    if (data?.chatHistory == null) {
        throw new Error("Expected response body from POST chat/history");
    }
    return data.chatHistory;
};

interface ClearChatHistoryParams {
    chatHistoryId: string;
}
const clearChatHistory = async ({ chatHistoryId }: ClearChatHistoryParams): Promise<void> => {
    if (!chatHistoryId) {
        throw new Error("Chat history ID is required");
    }

    return apiFetch<void>("chat/messages", {
        method: "DELETE",
        searchParams: {
            chat_history_id: chatHistoryId,
        },
        retry: 0,
    });
};

// Add message mutation
interface AddMessageParams {
    chatHistoryId: string | undefined;
    message: Omit<ChatMessage, "id">;
}
const addMessage = async ({ chatHistoryId, message }: AddMessageParams): Promise<ChatMessage> => {
    if (!chatHistoryId) {
        throw new Error("Chat history ID is required");
    }

    const data = await apiFetch<{ chatMessage: ChatMessage }>("chat/messages", {
        method: "POST",
        json: { chatHistoryId, message },
        retry: 0,
    });

    if (data?.chatMessage == null) {
        throw new Error("Expected response body from POST chat/messages");
    }
    return data.chatMessage;
};

// Update message mutation
interface UpdateMessageParams {
    chatHistoryId: string | undefined;
    messageId: string;
    message: Partial<ChatMessage>;
}
const updateMessage = async ({ chatHistoryId, messageId, message }: UpdateMessageParams): Promise<void> => {
    if (!chatHistoryId) {
        throw new Error("Chat history ID is required");
    }

    return apiFetch<void>(`chat/messages/${messageId}`, {
        method: "PATCH",
        json: { chatHistoryId, update: message },
        retry: 0,
    });
};

interface UpdateMessagesParams {
    chatHistoryId: string | undefined;
    match: Partial<Pick<ChatMessage, "actionStatus" | "actionIntent">>;
    update: Partial<ChatMessage>;
}
const updateMessages = async ({ chatHistoryId, match, update }: UpdateMessagesParams): Promise<void> => {
    if (!chatHistoryId) {
        throw new Error("Chat history ID is required");
    }

    return apiFetch<void>("chat/messages", {
        method: "PATCH",
        json: { chatHistoryId, match, update },
        retry: 0,
    });
};

export interface UseScoutChatHistoriesProps {
    patientId?: string;
}

export const useScoutChatHistories = (params: UseScoutChatHistoriesProps = {}) => {
    const queryClient = useQueryClient();

    // Query for fetching scout chat histories
    const query = useQuery({
        queryKey: ["scout-chat-histories", params],
        queryFn: async () => fetchScoutChatHistories(params),
        placeholderData: keepPreviousData, // keep previous data while refetching to avoid UI flickering
        staleTime: 30 * 1000, // 30 seconds - "stale" data is refetched automatically on window refocus
        refetchOnMount: "always", // force refetch on component mount
    });

    // Invalidate and refetch scout chat histories after successful mutation
    const invalidateScoutChatHistories = () =>
        queryClient.invalidateQueries({ queryKey: ["scout-chat-histories", params] });

    // Mutation to create a new chat history
    const createChatHistoryMutation = useMutation({
        mutationFn: createChatHistory,
        onSettled: invalidateScoutChatHistories,
    });
    const clearChatHistoryMutation = useMutation({
        mutationFn: clearChatHistory,
        onMutate: async () => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({ queryKey: ["scout-chat-histories", params] });

            // Optimistically update to the new value
            queryClient.setQueryData<ScoutChatHistory[]>(["scout-chat-histories", params], (old) => [
                {
                    id: old?.[0]?.id || "",
                    messages: [],
                },
            ]);
        },
        onSettled: invalidateScoutChatHistories,
    });
    const addMessageMutation = useMutation({
        mutationFn: addMessage,
        onMutate: async (variables) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({ queryKey: ["scout-chat-histories", params] });

            // Optimistically update to the new value
            queryClient.setQueryData<ScoutChatHistory[]>(["scout-chat-histories", params], (old) => [
                {
                    id: old?.[0]?.id || "",
                    messages: [
                        ...(old?.[0]?.messages || []),
                        {
                            id: Date.now().toString(),
                            ...variables.message,
                        },
                    ],
                },
            ]);
        },
        onSettled: invalidateScoutChatHistories,
    });
    const updateMessageMutation = useMutation({
        mutationFn: updateMessage,
        onMutate: async (variables) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({ queryKey: ["scout-chat-histories", params] });

            // Optimistically update to the new value
            queryClient.setQueryData<ScoutChatHistory[]>(["scout-chat-histories", params], (old) => [
                {
                    id: old?.[0]?.id || "",
                    messages: (old?.[0]?.messages || []).map((oldMsg) =>
                        oldMsg.id === variables.messageId
                            ? {
                                  ...oldMsg,
                                  ...variables.message,
                              }
                            : oldMsg,
                    ),
                },
            ]);
        },
        onSettled: invalidateScoutChatHistories,
    });
    const updateMessagesMutation = useMutation({
        mutationFn: updateMessages,
        onMutate: async (variables) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({ queryKey: ["scout-chat-histories", params] });

            // Optimistically update to the new value
            queryClient.setQueryData<ScoutChatHistory[]>(["scout-chat-histories", params], (old) => [
                {
                    id: old?.[0]?.id || "",
                    messages: (old?.[0]?.messages || []).map((oldMsg) =>
                        Object.entries(variables.match).every(
                            ([key, value]) => oldMsg[key as keyof ChatMessage] === value,
                        )
                            ? {
                                  ...oldMsg,
                                  ...variables.update,
                              }
                            : oldMsg,
                    ),
                },
            ]);
        },
        onSettled: invalidateScoutChatHistories,
    });

    return {
        query,
        createChatHistoryMutation,
        clearChatHistoryMutation,
        addMessageMutation,
        updateMessageMutation,
        updateMessagesMutation,
    };
};
